import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from '@core/auth/auth.service';
import { Attachment } from '@shared/models';

import { ApiParams, ApiRequest, ApiService } from './api.service';
import { DangerAlert } from '@shared/alert';
import { Store } from '@ngrx/store';
import {
  showAlertWhenTooLargeFiles,
  isFileTooLarge,
  MojoAttachmentService,
} from 'mojo-irp-attachments';

export interface AttachmentParams extends ApiParams {
  associationType?: string;
  associationModule?: string;
  associateModuleId?: number;
  candidateId?: number;
  name?: string;
  attachmentIds?: number[];
}

export enum AssociationType {
  RESUME = 'resume',
  IMAGE = 'image',
  LOGO = 'logo',
  HEADER = 'header',
  OTHERS = 'others',
  NOTE_FILES = 'note_files',
  REPORT = 'report_xlsx',
  COVER_LETTER = 'cover_letter',
  COVER_LETTER_PDF = 'cover_letter_pdf',
}

export enum AssociationModule {
  ASSOCIATION = 'Association',
  CANDIDATE = 'Candidate',
  COMPANY = 'Company',
  USER = 'User',
  JOB = 'Job',
  JOB_BOARD = 'JobBoard',
  NOTE = 'Note',
}

@Injectable()
export class AttachmentService extends ApiService<Attachment> implements MojoAttachmentService {
  static readonly AssociationType = AssociationType;
  static readonly AssociationModule = AssociationModule;
  private maxFileSizeInMB = 10;

  constructor(http: HttpClient, private auth: AuthService, private store: Store) {
    super(http);
    this.url = 'attachment/';
  }

  latestExcelReport(mojoUserId: number): Observable<Attachment> {
    const params: Partial<AttachmentParams> = {
      associateModuleId: mojoUserId,
      associationModule: AttachmentService.AssociationModule.USER,
      associationType: AttachmentService.AssociationType.REPORT,
    };

    return this.get({
      url: 'latest',
      params,
    });
  }

  latestResume(candidateId: number, onlyPdfFiles: boolean): Observable<Attachment> {
    return this.get({
      url: `candidates/${candidateId}/resumes/latest?onlyPdfFiles=${onlyPdfFiles}`,
    });
  }

  latestCoverLetter(candidateId: number, onlyPdfFiles: boolean): Observable<Attachment> {
    return this.get({
      url: `candidates/${candidateId}/coverletters/latest?onlyPdfFiles=${onlyPdfFiles}`,
    });
  }

  resumes({ candidateId }: { candidateId: number }): Observable<Attachment[]> {
    return this.get({ url: `candidates/${candidateId}/resumes?deduplicated=true` });
  }

  list(params: Partial<AttachmentParams>): Observable<Attachment[]> {
    return this.get({ url: 'list', params });
  }

  download({ id }: ApiRequest<AttachmentParams>): Observable<Blob> {
    return this.get({ id: `${id}/download`, responseType: 'blob' });
  }

  getUrl(id: number): Observable<string> {
    return this.get({ id: `${id}` }).pipe(map((item) => item.path));
  }

  save(file: File, attachmentParams: AttachmentParams): Observable<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(attachmentParams).forEach((key) => {
      formData.append(key, attachmentParams[key]);
    });

    return this.http.post<Attachment>(this.baseUrl, formData).pipe(catchError(this.handleError));
  }

  saveMultiple(files: File[], attachmentParams: AttachmentParams): Observable<Attachment[]> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    Object.keys(attachmentParams).forEach((key) => {
      formData.append(key, attachmentParams[key]);
    });

    return this.http
      .post<Attachment[]>(this.baseUrl + '?multiple', formData)
      .pipe(catchError(this.handleError));
  }

  updateModuleId(attachmentIds: number[], moduleId: number): Observable<any> {
    const params: Partial<AttachmentParams> = {
      attachmentIds,
      associateModuleId: moduleId,
    };
    return this.http
      .put<Attachment>(this.baseUrl + `module/${moduleId}`, params)
      .pipe(catchError(this.handleError));
  }

  update(file: File, id: number): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id', id.toString());

    return this.http
      .put<Attachment>(this.baseUrl + `${id}`, formData)
      .pipe(catchError(this.handleError));
  }

  showAlertWhenTooLargeFiles(fileNames, sumOfFileSizes = 0, areMultiple = false): boolean {
    return showAlertWhenTooLargeFiles(
      fileNames,
      (msg) => this.store.dispatch(new DangerAlert(msg)),
      sumOfFileSizes,
      areMultiple,
    );
  }

  isFileTooLarge(file: File): boolean {
    return isFileTooLarge(file);
  }
}
